import { z } from 'zod'
import { parseTimestamp } from '@/util/zod/validators'

/**
 * A draft's sticky template choice — one entry per artifact that has a selection, shared by all of
 * its versions. Drives the template button's state and pre-fills the select-template modal.
 * Referenced by id only; the display name is resolved from the matching UserFile.
 */
export const ArtifactTemplateSelectionReadSchema = z.object({
  artifact_id: z.string().uuid(),
  template_user_file_id: z.string().uuid(),
})
export type ArtifactTemplateSelectionRead = z.infer<typeof ArtifactTemplateSelectionReadSchema>

/**
 * Metadata of the template applied to a specific artifact version, plus the rendered docx. Lets a
 * later download/save serve that output directly instead of re-applying.
 *
 * `template_user_file_id` can be null: if the template is deleted after an export, the formatting
 * still happened and the output persists, so the entry stays (the name just won't resolve).
 */
export const ArtifactVersionAppliedTemplateReadSchema = z.object({
  artifact_version_id: z.string().uuid(),
  template_user_file_id: z.string().uuid().nullable(),
  applied_output_path: z.string(),
  // Server time the template was last (re)applied to this version. Latest-wins, so it moves forward
  // on every re-apply; compared against the version's `updated_at` to detect a stale output.
  last_applied_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})
export type ArtifactVersionAppliedTemplateRead = z.infer<typeof ArtifactVersionAppliedTemplateReadSchema>
